/**
 * core.people — HTTP client wrappers for the people workload hub.
 *
 * All endpoints live under the usersApi Cloud Function (/api/users/...).
 * Reuses getUsersApi() for apiBase + getAuthToken so the host only needs
 * to call setUsersApi() once at boot.
 *
 * Usage:
 *   import { peopleClient } from "@praesto/core";
 *   const leaves = await peopleClient.listLeaves({ userId: uid });
 */
import type {
  Leave,
  AvailabilityRule,
  AvailabilityRuleType,
  Skill,
  WorkloadAgg,
  UnavailabilityReason,
  WorkloadBoard,
} from "@praesto/shared-types";
import { getUsersApi } from "../users/client-singleton.js";

async function bearer(url: string, tok: string, init: RequestInit = {}): Promise<Response> {
  const headers: Record<string, string> = {
    Authorization: `Bearer ${tok}`,
    ...(init.headers as Record<string, string> | undefined),
  };
  if (init.body !== undefined) {
    headers["Content-Type"] = "application/json";
  }
  return fetch(url, { ...init, headers });
}

function qs(params: Record<string, string | undefined>): string {
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== "") usp.append(k, v);
  }
  const s = usp.toString();
  return s ? `?${s}` : "";
}

async function json<T>(r: Response, fallbackMsg: string): Promise<T> {
  if (!r.ok) {
    const err = (await r.json().catch(() => ({}))) as { error?: string };
    throw new Error(err.error ?? fallbackMsg);
  }
  return (await r.json()) as T;
}

// ---------------------------------------------------------------------------
// Singleton client object
// ---------------------------------------------------------------------------

export const peopleClient = {
  // -------------------------------------------------------------------------
  // Leaves
  // -------------------------------------------------------------------------

  async listLeaves(filters: {
    status?: string;
    userId?: string;
    hotelId?: string;
    from?: string;
    to?: string;
  }): Promise<Leave[]> {
    const api = getUsersApi();
    const tok = await api.getAuthToken();
    const r = await bearer(`${api.apiBase}/api/users/leaves${qs(filters)}`, tok);
    return (await json<{ leaves: Leave[] }>(r, "list_leaves_failed")).leaves;
  },

  async listLeavesForUser(uid: string): Promise<Leave[]> {
    const api = getUsersApi();
    const tok = await api.getAuthToken();
    const r = await bearer(`${api.apiBase}/api/users/${uid}/leaves`, tok);
    return (await json<{ leaves: Leave[] }>(r, "list_leaves_failed")).leaves;
  },

  async createLeave(input: {
    userId: string;
    hotelId: string;
    type: "vacation" | "permit" | "sick" | "leave";
    startDate: string;
    endDate: string;
    reason?: string;
  }): Promise<{ id: string }> {
    const api = getUsersApi();
    const tok = await api.getAuthToken();
    const r = await bearer(`${api.apiBase}/api/users/leaves`, tok, {
      method: "POST",
      body: JSON.stringify(input),
    });
    return json<{ id: string }>(r, "create_leave_failed");
  },

  async approveLeave(id: string): Promise<void> {
    const api = getUsersApi();
    const tok = await api.getAuthToken();
    const r = await bearer(`${api.apiBase}/api/users/leaves/${id}/approve`, tok, {
      method: "POST",
    });
    await json<unknown>(r, "approve_leave_failed");
  },

  async rejectLeave(id: string, rejectionReason: string): Promise<void> {
    const api = getUsersApi();
    const tok = await api.getAuthToken();
    const r = await bearer(`${api.apiBase}/api/users/leaves/${id}/reject`, tok, {
      method: "POST",
      body: JSON.stringify({ rejectionReason }),
    });
    await json<unknown>(r, "reject_leave_failed");
  },

  async cancelLeave(id: string): Promise<void> {
    const api = getUsersApi();
    const tok = await api.getAuthToken();
    const r = await bearer(`${api.apiBase}/api/users/leaves/${id}`, tok, {
      method: "DELETE",
    });
    await json<unknown>(r, "cancel_leave_failed");
  },

  // -------------------------------------------------------------------------
  // Availability rules
  // -------------------------------------------------------------------------

  async listAvailabilityRules(uid: string): Promise<AvailabilityRule[]> {
    const api = getUsersApi();
    const tok = await api.getAuthToken();
    const r = await bearer(`${api.apiBase}/api/users/${uid}/availability-rules`, tok);
    return (await json<{ rules: AvailabilityRule[] }>(r, "list_rules_failed")).rules;
  },

  async createAvailabilityRule(input: {
    userId: string;
    type: AvailabilityRuleType;
    dayOfWeek?: number;
    slot?: "M" | "P" | "N";
    validFrom?: string;
    validTo?: string;
    reason?: string;
  }): Promise<{ id: string }> {
    const api = getUsersApi();
    const tok = await api.getAuthToken();
    const r = await bearer(`${api.apiBase}/api/users/availability-rules`, tok, {
      method: "POST",
      body: JSON.stringify(input),
    });
    return json<{ id: string }>(r, "create_rule_failed");
  },

  async deleteAvailabilityRule(id: string): Promise<void> {
    const api = getUsersApi();
    const tok = await api.getAuthToken();
    const r = await bearer(`${api.apiBase}/api/users/availability-rules/${id}`, tok, {
      method: "DELETE",
    });
    await json<unknown>(r, "delete_rule_failed");
  },

  // -------------------------------------------------------------------------
  // Unavailability check
  // -------------------------------------------------------------------------

  async isUserUnavailable(
    uid: string,
    date: string,
    slot: "M" | "P" | "N",
  ): Promise<{ blocked: boolean; reasons: UnavailabilityReason[] }> {
    const api = getUsersApi();
    const tok = await api.getAuthToken();
    const r = await bearer(`${api.apiBase}/api/users/${uid}/unavailable${qs({ date, slot })}`, tok);
    return json<{ blocked: boolean; reasons: UnavailabilityReason[] }>(
      r,
      "unavailable_check_failed",
    );
  },

  // -------------------------------------------------------------------------
  // Skills taxonomy
  // -------------------------------------------------------------------------

  _skillsCache: undefined as Skill[] | undefined,

  async getSkillsTaxonomy(): Promise<Skill[]> {
    if (this._skillsCache !== undefined) return this._skillsCache;
    const api = getUsersApi();
    const tok = await api.getAuthToken();
    const r = await bearer(`${api.apiBase}/api/users/skills-taxonomy`, tok);
    const skills = (await json<{ skills: Skill[] }>(r, "skills_taxonomy_failed")).skills;
    this._skillsCache = skills;
    return skills;
  },

  // -------------------------------------------------------------------------
  // Workload
  // -------------------------------------------------------------------------

  async getWorkload(uid: string, weekStart: string, days: number): Promise<WorkloadAgg> {
    const api = getUsersApi();
    const tok = await api.getAuthToken();
    const r = await bearer(
      `${api.apiBase}/api/users/${uid}/workload${qs({ weekStart, days: String(days) })}`,
      tok,
    );
    return json<WorkloadAgg>(r, "workload_failed");
  },

  async getWorkloadBulk(
    uids: string[],
    weekStart: string,
    days: number,
  ): Promise<Record<string, WorkloadAgg>> {
    const api = getUsersApi();
    const tok = await api.getAuthToken();
    const r = await bearer(
      `${api.apiBase}/api/users/workload/bulk${qs({ uids: uids.join(","), weekStart, days: String(days) })}`,
      tok,
    );
    return (await json<{ workload: Record<string, WorkloadAgg> }>(r, "workload_bulk_failed"))
      .workload;
  },

  /**
   * Coverage-first Workload board (Hotel > Reparto > Persone). One round-trip;
   * the web app's buildBoardModel() does the grouping/coverage math.
   */
  async getWorkloadBoard(
    weekStart: string,
    days: number,
    hotelId?: string,
  ): Promise<WorkloadBoard> {
    const api = getUsersApi();
    const tok = await api.getAuthToken();
    const r = await bearer(
      `${api.apiBase}/api/users/workload-board${qs({ weekStart, days: String(days), hotelId })}`,
      tok,
    );
    return json<WorkloadBoard>(r, "workload_board_failed");
  },

  // -------------------------------------------------------------------------
  // Shifts (cross-module read — returns opaque shape, consumed by Shifts pkg)
  // -------------------------------------------------------------------------

  async getShiftsForUser(uid: string, weekStart: string, days: number): Promise<unknown[]> {
    const api = getUsersApi();
    const tok = await api.getAuthToken();
    const r = await bearer(
      `${api.apiBase}/api/users/${uid}/shifts${qs({ weekStart, days: String(days) })}`,
      tok,
    );
    return (await json<{ shifts: unknown[] }>(r, "shifts_for_user_failed")).shifts;
  },
};

export type PeopleClient = typeof peopleClient;
